// app/components/PurchaseModalPopup.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import { createOrder, validateOrder } from "../utils/server-api";
import { createOrderRecord } from "../actions/order";
import {
  paymentInitiatedEvent,
  purchaseEvent,
} from "../utils/gtm-events/contact";

import Cookies from "js-cookie";
import { useMiniCart } from "../context/MiniCartContext";
import { toast } from "react-toastify";

declare global {
  interface Window {
    Razorpay?: any;
    discountInfo?: DiscountInfo;
  }
}

// Global cache for first order status
let globalFirstOrderStatus: {
  isFirstOrder: boolean;
  timestamp: number;
} | null = null;
const CACHE_DURATION = 5 * 60 * 1000;

async function fetchFirstOrderStatus(): Promise<boolean> {
  if (
    globalFirstOrderStatus &&
    Date.now() - globalFirstOrderStatus.timestamp < CACHE_DURATION
  ) {
    return globalFirstOrderStatus.isFirstOrder;
  }

  try {
    const res = await fetch("/api/user", {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });

    if (!res.ok) {
      throw new Error(`API returned ${res.status}`);
    }

    const body = await res.json();
    const orderArray: Array<any> = body.orders || [];
    const isFirstOrder = orderArray.length === 0;

    globalFirstOrderStatus = {
      isFirstOrder,
      timestamp: Date.now(),
    };

    return isFirstOrder;
  } catch (error) {
    console.error("Error fetching first order status:", error);
    throw new Error("Unable to fetch order status");
  }
}

async function fetchUserData(): Promise<{
  addresses: UserAddress[];
  user: any;
}> {
  try {
    const res = await fetch("/api/user", {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });

    if (!res.ok) {
      throw new Error(`API returned ${res.status}`);
    }

    const body = await res.json();
    return {
      addresses: body.addresses || [],
      user: body.user || {},
    };
  } catch (error) {
    console.error("Error fetching user data:", error);
    return { addresses: [], user: {} };
  }
}

export function invalidateFirstOrderCache(): void {
  globalFirstOrderStatus = null;
}

interface NotificationProps {
  type: "success" | "error" | "info";
  message: string;
  onClose: () => void;
}

const Notification = ({ type, message, onClose }: NotificationProps) => (
  <div
    className={`fixed top-4 right-4 max-w-xs w-full p-3 rounded-lg shadow-lg border-l-4 text-sm ${
      type === "success"
        ? "bg-green-50 border-green-500 text-green-700"
        : type === "error"
          ? "bg-red-50 border-red-500 text-red-700"
          : "bg-orange-50 border-orange-500 text-orange-700"
    }`}
  >
    <div className="flex justify-between items-center">
      <p>{message}</p>
      <button onClick={onClose} className="ml-2 text-lg hover:opacity-70">
        ×
      </button>
    </div>
  </div>
);

interface UserAddress {
  id: number;
  label: string;
  fullName: string;
  streetAddress: string;
  apartment: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
  alternativePhoneNumber: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PurchaseModalPopupProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    price: number;
    currency?: string;
    currencySymbol?: string;
  };
  preOrder: boolean;
  subtotal: number;
  orderItems: any[];
  cartSessionId?: string;
}

interface FormData {
  name: string;
  email: string;
  mobile: string;
  addr1: string;
  addr2: string;
  city: string;
  stateProv: string;
  pin: string;
}

interface DiscountInfo {
  isFirstOrder: boolean;
  discountPercentage: number;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
}

export default function PurchaseModalPopup({
  isOpen,
  onClose,
  product,
  preOrder,
  subtotal,
  orderItems,
  cartSessionId,
}: PurchaseModalPopupProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    mobile: "",
    addr1: "",
    addr2: "",
    city: "",
    stateProv: "",
    pin: "",
  });
  const [tncChecked, setTncChecked] = useState(false);
  const [saveAddress, setSaveAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const [isFirstOrder, setIsFirstOrder] = useState<boolean | null>(null);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [showDiscountDetails, setShowDiscountDetails] = useState(false);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    null,
  );
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  const { decryptedGateway, activeGateway } = useMiniCart();
  const razorpayKey = useMemo(() => {
    return (
      decryptedGateway?.apiKey ||
      process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID ||
      null
    );
  }, [decryptedGateway?.apiKey]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    try {
      setIsLoadingAddress(true);

      // Fetch data in parallel
      const [firstOrderStatus, userData] = await Promise.all([
        isFirstOrder === null
          ? fetchFirstOrderStatus()
          : Promise.resolve(isFirstOrder),
        fetchUserData(),
      ]);

      if (firstOrderStatus !== isFirstOrder) {
        setIsFirstOrder(firstOrderStatus);
        calculateDiscount(firstOrderStatus);
      }

      setAddresses(userData.addresses);

      // Set up initial form data from API only
      setupInitialFormData(userData.addresses, userData.user);
    } catch (error) {
      console.error("Failed to load data:", error);
      showNotification(
        "error",
        "Unable to load your information. Please refresh.",
      );
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const setupInitialFormData = (userAddresses: UserAddress[], user: any) => {
    const defaultFormData: FormData = {
      name: "",
      email: "",
      mobile: "",
      addr1: "",
      addr2: "",
      city: "",
      stateProv: "",
      pin: "",
    };

    let updatedFormData = { ...defaultFormData };

    // Priority 1: Use user info from API for name/email
    if (user && (user.name || user.email)) {
      updatedFormData = {
        ...updatedFormData,
        name: user.name || "",
        email: user.email || "",
      };
    }

    // Priority 2: Use default address from API
    if (userAddresses.length > 0) {
      const defaultAddress =
        userAddresses.find((addr) => addr.isDefault) || userAddresses[0];
      if (defaultAddress) {
        updatedFormData = updateFormFromUserAddress(
          defaultAddress,
          updatedFormData,
        );
        setSelectedAddressId(defaultAddress.id);
      }
    }

    // Ensure all required fields have at least empty strings
    setFormData(updatedFormData);
  };

  const updateFormFromUserAddress = (
    address: UserAddress,
    currentFormData?: FormData,
  ): FormData => {
    const baseData = currentFormData || {
      name: "",
      email: "",
      mobile: "",
      addr1: "",
      addr2: "",
      city: "",
      stateProv: "",
      pin: "",
    };

    return {
      ...baseData,
      name: address.fullName?.trim() || baseData.name,
      mobile: address.phoneNumber?.trim() || baseData.mobile,
      addr1: address.streetAddress?.trim() || baseData.addr1,
      addr2: address.apartment?.trim() || baseData.addr2,
      city: address.city?.trim() || baseData.city,
      stateProv: address.state?.trim() || baseData.stateProv,
      pin: address.postalCode?.trim() || baseData.pin,
    };
  };

  const calculateDiscount = (firstOrder: boolean) => {
    const discountPercentage = parseInt(
      process.env.NEXT_PUBLIC_FIRST_ORDER_DISCOUNT_PERCENTAGE || "10",
    );
    const discountAmount = firstOrder
      ? (subtotal * discountPercentage) / 100
      : 0;
    const finalAmount = subtotal - discountAmount;

    const discountData = {
      isFirstOrder: firstOrder,
      discountPercentage: firstOrder ? discountPercentage : 0,
      discountAmount,
      originalAmount: subtotal,
      finalAmount,
    };

    setDiscountInfo(discountData);

    if (typeof window !== "undefined") {
      window.discountInfo = discountData;
    }
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => setScriptLoaded(true);
    script.onerror = () =>
      showNotification("error", "Payment SDK failed to load.");
    document.body.appendChild(script);

    return () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const showNotification = (
    type: "success" | "error" | "info",
    message: string,
  ) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const lookupPinCode = async (pincode: string) => {
    if (pincode.length !== 6) return;

    setPinLookupLoading(true);

    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );
      const data = await response.json();

      if (data[0]?.Status === "Success" && data[0]?.PostOffice) {
        const postOffice = data[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          city: postOffice.District || postOffice.Block || prev.city,
          stateProv: postOffice.State || prev.stateProv,
        }));
      }
    } catch (error) {
      console.error("PIN code lookup failed:", error);
    } finally {
      setPinLookupLoading(false);
    }
  };

  // International phone validation - accepts any country codes
  const isValidInternationalPhone = (phone: string): boolean => {
    // Remove all non-digit and non-plus characters for length check
    const digitsOnly = phone.replace(/[^\d+]/g, "");

    // Check if it starts with + (international format)
    if (digitsOnly.startsWith("+")) {
      // International format: + followed by country code and number
      // Must have at least 11 characters total (+ plus 10+ digits)
      return digitsOnly.length >= 11;
    } else {
      // Local format: check if it's 10 digits
      const digitCount = phone.replace(/[^0-9]/g, "").length;
      return digitCount === 10;
    }
  };

  const validateForm = (): boolean => {
    // Check all required fields are filled
    if (!formData.name?.trim()) {
      showNotification("error", "Please enter your name");
      return false;
    }

    if (
      !formData.email?.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      showNotification("error", "Please enter a valid email address");
      return false;
    }

    // Phone validation for any country codes
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,15}$/;
    const cleanPhone = formData.mobile?.replace(/[\s\-\(\)\.]/g, "") || "";
    const digitCount = cleanPhone.replace(/[^0-9]/g, "").length;

    if (
      !formData.mobile?.trim() ||
      !phoneRegex.test(formData.mobile) ||
      digitCount < 10
    ) {
      showNotification(
        "error",
        "Please enter a valid mobile number with at least 10 digits (e.g., +1 555 123 4567 or 9876543210)",
      );
      return false;
    }

    if (!formData.addr1?.trim()) {
      showNotification("error", "Please enter your address");
      return false;
    }

    if (!formData.city?.trim()) {
      showNotification("error", "Please enter your city");
      return false;
    }

    if (!formData.stateProv?.trim()) {
      showNotification("error", "Please enter your state");
      return false;
    }

    if (!formData.pin?.trim() || !/^\d{6}$/.test(formData.pin)) {
      showNotification("error", "Please enter a valid 6-digit PIN code");
      return false;
    }

    return true;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === "mobile") {
      // Allow +, numbers, spaces, hyphens, parentheses, and dots for international format
      const cleaned = value.replace(/[^\d\s+\-\(\)\.]/g, "");
      setFormData((prev) => ({ ...prev, [field]: cleaned }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const formatAddress = (): string =>
    [
      formData.addr1,
      formData.addr2,
      formData.city,
      formData.stateProv,
      formData.pin,
    ]
      .filter(Boolean)
      .join(", ");

  const handleSelectAddress = (address: UserAddress) => {
    const updatedFormData = updateFormFromUserAddress(address, formData);
    setFormData(updatedFormData);
    setSelectedAddressId(address.id);
    showNotification("success", "Address loaded!");
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isFirstOrder === null) {
      showNotification(
        "error",
        "Please wait while we verify your order status...",
      );
      return;
    }

    if (activeGateway?.checkoutPartner !== "razorpay") {
      toast.error("Active payment gateway is not Razorpay");
      return;
    }

    if (!tncChecked) {
      showNotification("error", "Please agree to the terms and conditions");
      return;
    }

    if (!scriptLoaded) {
      showNotification("error", "Payment system is loading. Please wait...");
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (!discountInfo) {
        throw new Error("Discount information not available");
      }

      const trackingPayload = JSON.stringify({
        utm_source: Cookies.get("utm_source") || null,
        utm_medium: Cookies.get("utm_medium") || null,
        utm_campaign: Cookies.get("utm_campaign") || null,
        utm_term: Cookies.get("utm_term") || null,
        utm_content: Cookies.get("utm_content") || null,
        user_agent: navigator.userAgent,
        landing_page_url: window.location.href,
        ip_address: null,
      });

      sessionStorage.setItem("discountInfo", JSON.stringify(discountInfo));

      const order = await createOrder(
        discountInfo.finalAmount,
        product.currency || "INR",
      );

      const orderResult = await createOrderRecord({
        userDetails: {
          name: formData.name,
          email: formData.email,
          phone: formData.mobile,
        },
        addresses: {
          shippingAddress: {
            addressLine1: formData.addr1,
            addressLine2: formData.addr2,
            city: formData.city,
            state: formData.stateProv,
            pincode: formData.pin,
          },
          billingAddress: {
            addressLine1: formData.addr1,
            addressLine2: formData.addr2,
            city: formData.city,
            state: formData.stateProv,
            pincode: formData.pin,
          },
        },
        orderItems:
          orderItems?.map((item: any) => ({
            productId: item.attributes.product.data.id,
            quantity: item.attributes.quantity,
            variantId: item.attributes.variant?.data?.id,
          })) || [],
        cartSessionId: cartSessionId,
        discountInfo: discountInfo,
        tracking: trackingPayload,
        transactionId: order.id,
      } as any);

      if (!orderResult.success) throw new Error(orderResult.message);

      paymentInitiatedEvent({
        pageLocation: window.location.pathname,
        amount: discountInfo.finalAmount,
        currency: product.currency || "INR",
        paymentMethod: "razorpay",
      });

      if ((order as any).statusCode === 401)
        throw new Error((order as any).error.description);
      if (!window.Razorpay) throw new Error("Payment SDK not initialized.");

      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Little Ginnie",
        description: preOrder ? "Pre-order" : "Purchase",
        order_id: order.id,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.mobile,
        },
        notes: {
          address: formatAddress(),
          isFirstOrder: discountInfo.isFirstOrder.toString(),
          discountApplied: discountInfo.discountAmount.toString(),
          originalAmount: discountInfo.originalAmount.toString(),
          orderId: orderResult.orderId,
          orderedItems:
            orderItems
              ?.map((item: any) => {
                const productTitle =
                  item.attributes.product.data.attributes.Title;
                const variantTitle =
                  item.attributes.variant?.data?.attributes?.title || "Default";
                const quantity = item.attributes.quantity;
                const price = item.attributes.unitPrice;
                return `${productTitle} (${variantTitle}) x${quantity} - ₹${price}`;
              })
              .join("; ") || "No items",
        },
        theme: { color: "000000" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            showNotification("info", "Payment cancelled. You can try again.");
          },
          escape: false,
          backdropclose: false,
        },
        handler: async (response: any) => {
          try {
            const vr = await validateOrder(response);
            if (vr.msg !== "success") throw new Error("Verification failed");

            purchaseEvent({
              orderId: response.razorpay_order_id,
              amount: discountInfo.finalAmount,
              currency: product.currency || "INR",
              pageLocation: window.location.pathname,
            });

            invalidateFirstOrderCache();

            if (preOrder) await handlePreOrder();

            showNotification("success", "Order placed successfully!");

            const discountParams = new URLSearchParams({
              isFirstOrder: discountInfo.isFirstOrder.toString(),
              discountPercentage: discountInfo.discountPercentage.toString(),
              discountAmount: discountInfo.discountAmount.toString(),
              originalAmount: discountInfo.originalAmount.toString(),
              finalAmount: discountInfo.finalAmount.toString(),
            }).toString();

            setTimeout(() => {
              window.location.href = `/thankyou?orderId=${orderResult?.orderId}&paymentId=${response.razorpay_payment_id}&status=success&${discountParams}`;
            }, 1000);
          } catch (err: any) {
            showNotification("error", err.message || "Order processing failed");
          } finally {
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("modal.open", () => {
        setTimeout(() => {
          const modal = document.querySelector(".razorpay-container");
          if (modal && discountInfo) {
            const discountElement = document.createElement("div");
            discountElement.className = "razorpay-discount-info";
            discountElement.style.cssText = `
              background: #f0fdf4;
              border: 1px solid #86efac;
              border-radius: 8px;
              padding: 12px;
              margin: 16px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            `;

            discountElement.innerHTML = `
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <strong style="color: #065f46; font-size: 14px;">Order Summary</strong>
                  <div style="font-size: 12px; color: #374151; margin-top: 4px;">
                    ${
                      discountInfo.isFirstOrder
                        ? `<span>🎉 First Order Discount Applied: ${discountInfo.discountPercentage}% OFF</span>`
                        : `<span>Regular Order</span>`
                    }
                  </div>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 16px; font-weight: bold; color: #000;">
                    ${currencySymbol} ${discountInfo.finalAmount.toLocaleString("en-IN")}
                  </div>
                  ${
                    discountInfo.isFirstOrder
                      ? `<div style="font-size: 11px; color: #059669; margin-top: 2px;">
                      Saved ${currencySymbol}${discountInfo.discountAmount.toLocaleString("en-IN")}
                    </div>`
                      : ""
                  }
                </div>
              </div>
            `;

            const modalBody = modal.querySelector(".modal-body") || modal;
            if (modalBody.firstChild) {
              modalBody.insertBefore(discountElement, modalBody.firstChild);
            }
          }
        }, 500);
      });

      rzp.on("payment.failed", (err: any) => {
        setLoading(false);
        showNotification("error", err.error?.description || "Payment failed");

        if (typeof window !== "undefined" && window.dataLayer) {
          window.dataLayer.push({
            event: "payment_failed",
            errorMessage: err.error?.description || "Payment failed",
            amount: discountInfo.finalAmount,
            currency: product.currency || "INR",
            discountInfo: discountInfo,
          });
        }
      });

      rzp.on("close", () => {
        setLoading(false);
      });

      setTimeout(() => {
        if (loading) {
          setLoading(false);
          showNotification(
            "error",
            "Payment could not be completed. Please try again.",
          );
        }
      }, 30000);

      rzp.open();
    } catch (err: any) {
      showNotification("error", err.message || "Checkout failed");
      setLoading(false);
    }
  };

  const handlePreOrder = async (): Promise<void> => {
    const strapiUrl =
      process.env.AICODEPRO_API_URL || process.env.NEXT_PUBLIC_STRAPI_URL || "";
    await fetch(`${strapiUrl}/api/web-builder/preorders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_FORM_TOKEN}`,
      },
      body: JSON.stringify({ data: { ...formData, address: formatAddress() } }),
    });
  };

  if (!isOpen) return null;

  const disabled =
    !tncChecked || loading || !scriptLoaded || isFirstOrder === null;
  const currencySymbol = product.currencySymbol ?? "₹";

  if (isFirstOrder === null) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            <p className="text-gray-600">Verifying your order status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl max-h-[95vh] overflow-auto">
        <div className="p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-black">
              {preOrder ? "Pre-Order" : "Checkout"}
            </h2>
            <button onClick={onClose} className="text-xl hover:opacity-70">
              ×
            </button>
          </div>

          {/* Discount Section */}
          {discountInfo && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Order Summary
                </h3>
                <button
                  onClick={() => setShowDiscountDetails(!showDiscountDetails)}
                  className="text-xs text-gray-500 hover:text-black"
                >
                  {showDiscountDetails ? "Hide Details" : "Show Details"}
                </button>
              </div>

              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-700">
                  {currencySymbol}{" "}
                  {discountInfo.originalAmount.toLocaleString("en-IN")}
                </span>
              </div>

              {showDiscountDetails && discountInfo.isFirstOrder && (
                <div className="pl-4 border-l-2 border-green-200 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500">
                      First Order Eligibility:
                    </span>
                    <span className="text-xs font-medium text-green-600">
                      ✓ Eligible
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500">
                      Discount Rate:
                    </span>
                    <span className="text-xs font-medium text-green-600">
                      {discountInfo.discountPercentage}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500">
                      Discount Calculation:
                    </span>
                    <span className="text-xs font-medium text-green-600">
                      {currencySymbol}
                      {discountInfo.originalAmount.toLocaleString(
                        "en-IN",
                      )} × {discountInfo.discountPercentage}%
                    </span>
                  </div>
                </div>
              )}

              {discountInfo.isFirstOrder && (
                <div className="flex justify-between items-center mb-2 bg-green-50 p-2 rounded">
                  <span className="text-sm text-green-700 font-medium">
                    First Order Discount ({discountInfo.discountPercentage}%):
                  </span>
                  <span className="text-sm font-bold text-green-700">
                    -{currencySymbol}{" "}
                    {discountInfo.discountAmount.toLocaleString("en-IN")}
                  </span>
                </div>
              )}

              <div className="border-t border-gray-200 my-2"></div>

              <div className="flex justify-between items-center pt-2">
                <span className="text-base font-semibold text-gray-800">
                  {discountInfo.isFirstOrder
                    ? "Discounted Total:"
                    : "Total Amount:"}
                </span>
                <div className="text-right">
                  <div className="text-lg font-bold text-black">
                    {currencySymbol}{" "}
                    {discountInfo.finalAmount.toLocaleString("en-IN")}
                  </div>
                  {discountInfo.isFirstOrder && (
                    <div className="text-xs text-green-600 mt-1">
                      After {discountInfo.discountPercentage}% discount
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-500">
                <p>
                  <span className="font-medium">Payment Method:</span> Razorpay
                  Gateway
                </p>
                <p className="mt-1">
                  <span className="font-medium">Note:</span> The discount of{" "}
                  {currencySymbol}
                  {discountInfo.discountAmount.toLocaleString("en-IN")} will be
                  applied to your final payment.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleCheckout} className="space-y-3 text-xs">
            {/* Personal Info */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="text"
                  placeholder="Name*"
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                  required
                />
              </div>

              <div>
                <input
                  type="email"
                  placeholder="Email*"
                  value={formData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                  required
                />
              </div>
            </div>

            <input
              type="tel"
              placeholder="Mobile* (e.g., +1 555 123 4567 or 9876543210)"
              value={formData.mobile || ""}
              onChange={(e) => handleInputChange("mobile", e.target.value)}
              className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
              required
            />

            {/* Address Quick Select (if addresses exist) */}
            {addresses.length > 0 && (
              <div className="mb-2">
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Quick Address Select:
                </label>
                <div className="flex flex-wrap gap-2">
                  {addresses.map((address) => (
                    <button
                      key={address.id}
                      type="button"
                      onClick={() => handleSelectAddress(address)}
                      className={`px-3 py-1 text-xs rounded-full border transition ${
                        selectedAddressId === address.id
                          ? "bg-orange-100 border-orange-500 text-orange-700"
                          : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {address.label} {address.isDefault && "★"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Address Fields - Always visible */}
            <div>
              <label className="text-xs text-gray-600 font-medium mb-1 block">
                Address Details:
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Address Line 1*"
                  value={formData.addr1 || ""}
                  onChange={(e) => handleInputChange("addr1", e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                  required
                />

                <input
                  type="text"
                  placeholder="Address Line 2 (Optional)"
                  value={formData.addr2 || ""}
                  onChange={(e) => handleInputChange("addr2", e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                />

                <div className="relative">
                  <input
                    type="text"
                    placeholder="PIN Code*"
                    value={formData.pin || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      handleInputChange("pin", value);
                      if (value.length === 6) lookupPinCode(value);
                    }}
                    className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black pr-10"
                    required
                  />
                  {pinLookupLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-black"></div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="text"
                      placeholder="City*"
                      value={formData.city || ""}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                      required
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      placeholder="State*"
                      value={formData.stateProv || ""}
                      onChange={(e) =>
                        handleInputChange("stateProv", e.target.value)
                      }
                      className="w-full px-3 py-2 rounded border border-gray-300 bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-black"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* T&C - Removed Save Address checkbox */}
            <div className="flex items-center pt-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={tncChecked}
                  onChange={() => setTncChecked(!tncChecked)}
                  className="h-3 w-3 accent-orange-500 bg-white"
                  required
                />
                <span className="text-gray-600">
                  I agree to the{" "}
                  <a
                    href="/terms-and-conditions"
                    className="text-black underline"
                    target="_self"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>
                  *
                </span>
              </label>
            </div>

            {/* Action Button */}
            <div className="pt-3">
              <button
                type="submit"
                disabled={disabled}
                className={`w-full py-3 rounded text-sm font-medium transition ${
                  disabled
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-orange-500 text-white hover:bg-orange-800"
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-white"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </span>
                ) : preOrder ? (
                  "Pre-Order"
                ) : (
                  `Pay ${currencySymbol} ${discountInfo?.finalAmount.toLocaleString("en-IN")}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
